import React, { useState, useRef, useEffect } from "react";
import { Rnd } from "react-rnd";
import JoditEditor from "jodit-react";
import { useReactToPrint } from "react-to-print";
import { Trash2, Plus, Edit2, Check, X } from "lucide-react";

import {
  DiagramacaoPreviewProps,
  QuestionPage,
  PageConfig,
  PageElement,
  Page,
} from "./types/types";
import { usePageEditor } from "./hooks/usePageEditor";

import { QuestionDetails } from "../../../../../hooks/questions/types/types";

import { defaultEditorConfig } from "../../../../../constants/configJodit";

import "./DiagramacaoPreview.css";

// ──────────────────────────────────────────────
// CONSTANTES (A4 e layout)
// ──────────────────────────────────────────────

const A4_WIDTH = 792;
const A4_HEIGHT = 1119.5;
const COLUMN_GAP = 20;
const QUESTION_MARGIN = 15;

<style>
  {`
    @page {
      size: A4;
      margin: 0;
    }
    @media print {
      html, body {
        width: 100%;
        height: 100%;
        margin: 0;
        padding: 0;
      }
      .container-fluid {
        padding: 0;
        margin: 0;
      }
      .print-container {
        width: ${A4_WIDTH}px !important;
        height: ${A4_HEIGHT}px !important;
      }
      .no-print {
        display: none !important;
      }
    }
  `}
</style>;

// ──────────────────────────────────────────────
// COMPONENTE PRINCIPAL
// ──────────────────────────────────────────────

const DiagramacaoPreview: React.FC<DiagramacaoPreviewProps> = (props) => {
  const {
    nomeProva,
    provaSelect,
    numeroColunas,
    fontSizeQuestoes,
    marginSuperior,
    marginInferior,
    marginEsquerda,
    marginDireita,
    loading,
    questionPages,
    setQuestionPages,
    setLoading,
    randomAlternatives,
    randomQuestion,
    DataFrontPage,
    setDataFrontPage,
    fontSizeAlternatives,
    borderVisible,
    editMode,
  } = props;

  // ─────────────────────
  // ESTADOS LOCAIS
  // ─────────────────────

  const [selectedElement, setSelectedElement] = useState<number | null>(null);
  const [showEditor, setShowEditor] = useState<boolean>(false);
  const [tempContent, setTempContent] = useState<string>("");
  const [pageColumns, setPageColumns] = useState<number[]>([]);
  const [showPageEditor, setShowPageEditor] = useState<boolean>(false);
  const [pageEditorContent, setPageEditorContent] = useState<string>("");
  const [pageBeingEdited, setPageBeingEdited] = useState<number | null>(null);
  const [pageEditorColumns, setPageEditorColumns] =
    useState<number>(numeroColunas);
  const [pageConfigs, setPageConfigs] = useState<Record<number, PageConfig>>(
    {}
  );

  // ──────────────────────────────────────────────
  // REFERÊNCIAS
  // ──────────────────────────────────────────────

  const editorRef = useRef(null);
  const printRef = useRef<HTMLDivElement | null>(null);

  console.log("printRef", printRef);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: nomeProva,
  });

  const {
    handleAddElement,
    handleColumnChange,
    handleEditElement,
    handleEditorChange,
    handleConfirmEdit,
    handleCancelEdit,
    handleRemoveElement,
    handleDragStopCover,
    handleResizeCover,
    handleInlineEdit,
    handleDragStopQuestion,
    handleResizeQuestion,
    handleEditPage,
    handleConfirmEditPage,
    handleCancelEditPage,
  } = usePageEditor(
    pageBeingEdited,
    setPageBeingEdited,
    pageEditorContent,
    setPageEditorContent,
    pageEditorColumns,
    setPageEditorColumns,
    showPageEditor,
    setShowPageEditor,
    pageConfigs,
    setPageConfigs,
    selectedElement ?? 0,
    setSelectedElement,
    tempContent,
    setTempContent,
    showEditor,
    setShowEditor,
    DataFrontPage,
    setDataFrontPage,
    questionPages,
    setQuestionPages,
    pageColumns,
    setPageColumns,
    numeroColunas,
    fontSizeQuestoes,
    QUESTION_MARGIN,
    recalcPages, // recalcPages
    recalcPagesFromQuestionPage, // recalcPagesEdit (se não houver uma lógica específica, pode reutilizar)
    recalcPagesFromQuestionPage, // recalcPagesFromQuestionPage
    editMode // editMode
  );

  // ──────────────────────────────────────────────
  // FUNÇÃO UTILITÁRIA: Embaralhar array
  // ──────────────────────────────────────────────

  const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Configuração comum para o layout
  const config = {
    effectiveHeight: A4_HEIGHT - (marginSuperior + marginInferior),
    effectiveWidth: A4_WIDTH - (marginEsquerda + marginDireita),
    fontSizeQuestoes:
      typeof fontSizeQuestoes === "number"
        ? fontSizeQuestoes
        : fontSizeQuestoes === "default"
        ? 13
        : parseInt(fontSizeQuestoes),
    fontSizeAlternatives:
      typeof fontSizeAlternatives === "number"
        ? fontSizeAlternatives
        : fontSizeAlternatives === "default"
        ? 13
        : parseInt(fontSizeAlternatives),
    marginSuperior,
    marginInferior,
    marginEsquerda,
    marginDireita,
    numeroColunas,
    randomAlternatives,
    randomQuestion,
    DataFrontPage,
  };
  console.log("Configuração atual:", config);

  // ──────────────────────────────────────────────
  // FUNÇÃO recalcPages (modo normal – reconstrói as páginas a partir das questões)
  // ──────────────────────────────────────────────

  function recalcPages() {
    console.log("Iniciando recalcPages (modo normal)");

    if (!provaSelect?.questions) {
      console.log("Não há questões na prova");
      setQuestionPages([]);
      return;
    }

    // Função para processar tags <img>
    function processImgHTML(html: string): string {
      return html.replace(/<img([^>]*?)>/gi, (match, group1) => {
        const srcMatch = group1.match(/src=["']([^"']*)["']/i);
        const srcValue = srcMatch ? srcMatch[1] : "";
        if (!srcValue.trim()) return "";
        return `<img${group1} style="max-width:100%; height:auto;">`;
      });
    }

    // Classe ContentManager que utiliza as configurações do formulário
    class ContentManager {
      pages: QuestionPage[] = [];
      currentPage: QuestionPage;
      currentColumn = 0;
      remainingColumnHeights: number[];
      currentPageColumns: number;

      constructor() {
        this.currentPageColumns =
          pageColumns.length > 0 ? pageColumns[0] : numeroColunas;
        this.currentPage = { elements: [], columns: this.currentPageColumns };
        this.remainingColumnHeights = Array(this.currentPageColumns).fill(
          config.effectiveHeight
        );
        console.log("ContentManager iniciado:", {
          currentPageColumns: this.currentPageColumns,
          remainingColumnHeights: this.remainingColumnHeights,
        });
      }

      private createTemporaryDiv(): HTMLDivElement {
        const div = document.createElement("div");
        div.style.width = `${this.getColumnWidth(this.currentPageColumns)}px`;
        div.style.position = "absolute";
        div.style.visibility = "hidden";
        document.body.appendChild(div);
        return div;
      }

      private getColumnWidth(columns: number): number {
        return columns === 1
          ? config.effectiveWidth
          : (config.effectiveWidth - COLUMN_GAP * (columns - 1)) / columns;
      }

      measureContent(
        content: string,
        fontSize: number,
        columnWidth?: number
      ): number {
        const tempDiv = this.createTemporaryDiv();
        tempDiv.style.fontSize = `${fontSize}px`;
        if (columnWidth) tempDiv.style.width = `${columnWidth}px`;
        tempDiv.innerHTML = content;
        const height = tempDiv.offsetHeight;
        document.body.removeChild(tempDiv);
        return height + QUESTION_MARGIN;
      }

      private findBestBreakPoint(
        content: string,
        fontSize: number,
        maxHeight: number,
        columnWidth: number
      ): { fit: string; remaining: string } {
        const tempDiv = this.createTemporaryDiv();
        tempDiv.style.fontSize = `${fontSize}px`;
        tempDiv.style.width = `${columnWidth}px`;

        const paragraphs = content.split("\n");
        let fitContent = "";
        let currentContent = "";
        let remainingContent = "";

        for (let i = 0; i < paragraphs.length; i++) {
          const testContent =
            currentContent + (currentContent ? "\n" : "") + paragraphs[i];
          tempDiv.innerHTML = testContent;
          if (tempDiv.offsetHeight + QUESTION_MARGIN <= maxHeight) {
            currentContent = testContent;
            fitContent = currentContent;
          } else {
            if (i === 0) {
              const sentences = paragraphs[0].split(". ");
              currentContent = "";
              for (let j = 0; j < sentences.length; j++) {
                const testSentence =
                  currentContent + (currentContent ? ". " : "") + sentences[j];
                tempDiv.innerHTML = testSentence;
                if (tempDiv.offsetHeight + QUESTION_MARGIN <= maxHeight) {
                  currentContent = testSentence;
                  fitContent = currentContent;
                } else {
                  if (j === 0) {
                    const words = sentences[0].split(" ");
                    currentContent = "";
                    for (let k = 0; k < words.length; k++) {
                      const testWord =
                        currentContent + (currentContent ? " " : "") + words[k];
                      tempDiv.innerHTML = testWord;
                      if (tempDiv.offsetHeight + QUESTION_MARGIN <= maxHeight) {
                        currentContent = testWord;
                        fitContent = currentContent;
                      } else {
                        break;
                      }
                    }
                    remainingContent =
                      words.slice(currentContent.split(" ").length).join(" ") +
                      (sentences.length > 1
                        ? ". " + sentences.slice(1).join(". ")
                        : "") +
                      (paragraphs.length > 1
                        ? "\n" + paragraphs.slice(1).join("\n")
                        : "");
                  } else {
                    remainingContent =
                      sentences.slice(j).join(". ") +
                      (paragraphs.length > 1
                        ? "\n" + paragraphs.slice(1).join("\n")
                        : "");
                  }
                  break;
                }
              }
            } else {
              remainingContent = paragraphs.slice(i).join("\n");
            }
            break;
          }
        }

        document.body.removeChild(tempDiv);
        console.log("findBestBreakPoint:", {
          fit: fitContent,
          remaining: remainingContent.trim(),
        });
        return {
          fit: fitContent,
          remaining: remainingContent.trim(),
        };
      }

      private moveToNextColumn(): boolean {
        if (this.currentColumn < this.currentPageColumns - 1) {
          this.currentColumn++;
          console.log("Mudando para a próxima coluna:", this.currentColumn);
          return true;
        }
        return false;
      }

      private createNewPage() {
        console.log("Criando nova página");
        if (this.currentPage.elements.length > 0) {
          this.pages.push(this.currentPage);
          console.log("Página adicionada:", this.currentPage);
        }
        const nextPageIndex = this.pages.length;
        this.currentPageColumns =
          (pageConfigs && pageConfigs[nextPageIndex]?.columns) || numeroColunas;
        this.currentPage = { elements: [], columns: this.currentPageColumns };
        this.remainingColumnHeights = Array(this.currentPageColumns).fill(
          config.effectiveHeight
        );
        this.currentColumn = 0;
      }

      private calculateElementPosition(height: number): { x: number; y: number } {
        const columnWidth = this.getColumnWidth(this.currentPageColumns);
        const usedHeight =
          config.effectiveHeight -
          this.remainingColumnHeights[this.currentColumn];
        return {
          x: marginEsquerda + (columnWidth + COLUMN_GAP) * this.currentColumn,
          y: marginSuperior + usedHeight,
        };
      }

      addElement(content: string, fontSize: number) {
        console.log("Adicionando elemento com conteúdo:", content);
        let remainingContent = content;
        while (remainingContent) {
          const columnWidth = this.getColumnWidth(this.currentPageColumns);
          const { fit, remaining } = this.findBestBreakPoint(
            remainingContent,
            fontSize,
            this.remainingColumnHeights[this.currentColumn],
            columnWidth
          );
          if (fit) {
            const fitHeight = this.measureContent(
              `<div style="font-size: ${fontSize}px">${fit}</div>`,
              fontSize,
              columnWidth
            );
            const position = this.calculateElementPosition(fitHeight);
            this.currentPage.elements.push({
              element: `<div style="font-size: ${fontSize}px">${fit}</div>`,
              x: position.x,
              y: position.y,
              width: columnWidth,
              height: fitHeight,
            });
            console.log(
              "Elemento adicionado na coluna",
              this.currentColumn,
              "posição:",
              position,
              "altura:",
              fitHeight
            );
            this.remainingColumnHeights[this.currentColumn] -= fitHeight;
            remainingContent = remaining;
          }
          if (remaining && (!fit || remaining !== remainingContent)) {
            if (!this.moveToNextColumn()) {
              console.log("Passou do limite da coluna, criando nova página.");
              this.createNewPage();
            }
          } else if (!remaining) {
            break;
          }
        }
      }

      getPages(): QuestionPage[] {
        if (this.currentPage.elements.length > 0) {
          this.pages.push(this.currentPage);
        }
        console.log("Páginas geradas:", this.pages);
        return this.pages;
      }
    }

    const manager = new ContentManager();

    // Se randomQuestion estiver ativo, embaralha as questões
    const questions: QuestionDetails[] = randomQuestion
      ? shuffleArray(provaSelect.questions)
      : provaSelect.questions;

    console.log("Total de questões:", questions.length);

    questions.forEach((question, index) => {
      console.log(`Processando questão ${index + 1}`);
      // Se a questão possui um statement e subQuestions, ela é considerada "contextual"
      const hasContext =
        question.statement &&
        question.subQuestions &&
        question.subQuestions.length > 0;
      if (hasContext) {
        // Adiciona o statement como contexto (sem numeração)
        manager.addElement(
          `<div data-element-type="context"> ${question.statement}</div>`,
          typeof fontSizeQuestoes === "number" ? fontSizeQuestoes : 13
        );
        // Processa cada subQuestão usando numeração local (1, 2, 3...)
        question.subQuestions.forEach((subQ: any, subIndex: number) => {
          let processedSub = subQ.statement;
          if (
            /<img\s+[^>]*src=["']([^"']+)["'][^>]*>/i.test(subQ.statement || "")
          ) {
            processedSub = processImgHTML(subQ.statement || "");
          }
          manager.addElement(
            `<div data-element-type="question" style="margin-left: ${marginEsquerda/3}px; margin-right: ${marginDireita}px; margin-bottom: ${marginSuperior}px; margin-top: ${marginSuperior}px;"><strong>${
              subIndex + 1
            }.</strong> ${processedSub}</div>`,
            typeof fontSizeQuestoes === "number" ? fontSizeQuestoes : 13
          );
          if (subQ.objectives && subQ.objectives.length > 0) {
            subQ.objectives.forEach((obj: any, altIndex: number) => {
              let processedObj = obj.text;
              if (
                /<img\s+[^>]*src=["']([^"']+)["'][^>]*>/i.test(obj.text || "")
              ) {
                processedObj = processImgHTML(obj.text || "");
              }
              const letterLabel = String.fromCharCode(97 + altIndex);
              manager.addElement(
                `<div data-element-type="alternative" style="margin-left: ${marginEsquerda}px; margin-right: ${marginDireita}px; margin-bottom: ${marginSuperior}px; margin-top: ${marginSuperior}px;"><strong>${letterLabel})</strong> ${processedObj}</div>`,
                typeof fontSizeQuestoes === "number" ? fontSizeQuestoes : 13
              );
            });
          }
        });
      } else {
        if (
          /<img\s+[^>]*src=["']([^"']+)["'][^>]*>/i.test(question.statement || "")
        ) {
          const processedStatement = processImgHTML(question.statement || "");
          manager.addElement(
            `<div data-element-type="question"><strong>${
              index + 1
            }.</strong> ${processedStatement}</div>`,
            typeof fontSizeQuestoes === "number" ? fontSizeQuestoes : 13
          );
        } else {
          const questionText =
            question.statement ||
            (question.subQuestions[0] ? question.subQuestions[0].statement : "");
          let processedQuestionText =
            typeof questionText === "string"
              ? processImgHTML(questionText)
              : questionText;
          if (
            typeof questionText === "string" &&
            /<img\s+[^>]*src=["']([^"']+)["'][^>]*>/i.test(questionText)
          ) {
            processedQuestionText = processImgHTML(questionText);
          }
          manager.addElement(
            `<div data-element-type="question"><strong>${
              index + 1
            }.</strong> ${processedQuestionText}</div>`,
            typeof fontSizeQuestoes === "number" ? fontSizeQuestoes : 13
          );
          const firstSub = question.subQuestions[0];
          if (firstSub) {
            if (firstSub.objectives && firstSub.objectives.length > 0) {
              firstSub.objectives.forEach((obj: any, altIndex: number) => {
                let processedObj = obj.text;
                if (
                  /<img\s+[^>]*src=["']([^"']+)["'][^>]*>/i.test(obj.text || "")
                ) {
                  processedObj = processImgHTML(obj.text || "");
                }
                const letterLabel = String.fromCharCode(97 + altIndex);
                manager.addElement(
                  `<div data-element-type="alternative" style="margin-left: ${marginEsquerda}px; margin-right: ${marginDireita}px; margin-bottom: ${marginSuperior}px; margin-top: ${marginSuperior}px;"><strong>${letterLabel})</strong> ${processedObj}</div>`,
                  typeof fontSizeQuestoes === "number" ? fontSizeQuestoes : 13
                );
              });
            } else if (firstSub.dissertative) {
              manager.addElement(
                `<div data-element-type="question"><div style="min-height: 100px; border: 1px dashed #ccc;"></div></div>`,
                typeof fontSizeQuestoes === "number" ? fontSizeQuestoes : 13
              );
            }
          }
        }
        if (index < questions.length - 1) {
          manager.addElement(
            `<div data-element-type="separator" style="border-bottom: 1px solid black; margin: 10px 0;"></div>`,
            typeof fontSizeQuestoes === "number" ? fontSizeQuestoes : 13
          );
        }
      }
    });

    const pages = manager.getPages();
    console.log("Total de páginas geradas:", pages.length);
    setQuestionPages(pages);
    setLoading(false);
  }

  function recalcPagesEdit(): void {
    console.log(
      "Recalculando páginas em modo de edição usando JSON do questionPages..."
    );
    if (!questionPages || questionPages.length === 0) {
      console.log("Nenhuma página encontrada para editar");
      return;
    }

    const newPages: QuestionPage[] = questionPages.map((page: any) => {
      const columns = page.columns || numeroColunas;
      const newElements = page.elements.map((el: any) => {
        let newHtml = el.element;
        let appliedFontSize = 13;
        if (newHtml.includes('data-element-type="alternative"')) {
          appliedFontSize =
            typeof fontSizeAlternatives === "number" ? fontSizeAlternatives : 13;
          // Converte numeração numérica para letra (ex.: "1)" para "a)")
          newHtml = newHtml.replace(/<strong>(\d+)\)/, (_:any, num:any) => {
            const letter = String.fromCharCode(96 + parseInt(num, 10));
            return `<strong>${letter})</strong>`;
          });
        } else if (newHtml.includes('data-element-type="question"')) {
          appliedFontSize =
            typeof fontSizeQuestoes === "number" ? fontSizeQuestoes : 13;
        }
        newHtml = newHtml
          .replace(/margin-left:\s*[^;"']+px/, `margin-left: ${marginEsquerda}px`)
          .replace(
            /margin-right:\s*[^;"']+px/,
            `margin-right: ${marginDireita}px`
          )
          .replace(
            /margin-bottom:\s*[^;"']+px/,
            `margin-bottom: ${marginInferior}px`
          )
          .replace(/margin-top:\s*[^;"']+px/, `margin-top: ${marginSuperior}px`)
          .replace(/font-size:\s*[^;"']+px/, `font-size: ${appliedFontSize}px`);
        return {
          element: newHtml,
          x: el.x,
          y: el.y,
          width: el.width,
          height: el.height,
        };
      });
      return {
        elements: newElements,
        columns: columns,
      };
    });

    console.log("Páginas reconstruídas:", newPages);
    setQuestionPages(newPages);
    setLoading(false);
  }


  // ──────────────────────────────────────────────
  // FUNÇÃO recalcPagesFromQuestionPage (recalcula com base no JSON e nova configuração de colunas)
  // ──────────────────────────────────────────────

  function recalcPagesFromQuestionPage() {
    console.log(
      "Recalculando páginas com base em questionPages e nova configuração de colunas"
    );
    if (!questionPages || questionPages.length === 0) {
      console.log("Nenhuma página encontrada para editar");
      return;
    }

    class ContentManager {
      pages: QuestionPage[] = [];
      currentPage: QuestionPage;
      currentColumn = 0;
      remainingColumnHeights: number[];
      currentPageColumns: number;

      constructor(initialColumns?: number) {
        this.currentPageColumns =
          (pageConfigs && pageConfigs[0]?.columns) ||
          initialColumns ||
          numeroColunas;
        this.currentPage = { elements: [], columns: this.currentPageColumns };
        this.remainingColumnHeights = Array(this.currentPageColumns).fill(
          config.effectiveHeight
        );
        console.log("ContentManager iniciado:", {
          currentPageColumns: this.currentPageColumns,
          remainingColumnHeights: this.remainingColumnHeights,
        });
      }

      private createTemporaryDiv(): HTMLDivElement {
        const div = document.createElement("div");
        div.style.width = `${this.getColumnWidth(this.currentPageColumns)}px`;
        div.style.position = "absolute";
        div.style.visibility = "hidden";
        document.body.appendChild(div);
        return div;
      }

      private getColumnWidth(columns: number): number {
        return columns === 1
          ? config.effectiveWidth
          : (config.effectiveWidth - COLUMN_GAP * (columns - 1)) / columns;
      }

      measureContent(
        content: string,
        fontSize: number,
        columnWidth?: number
      ): number {
        const tempDiv = this.createTemporaryDiv();
        tempDiv.style.fontSize = `${fontSize}px`;
        if (columnWidth) tempDiv.style.width = `${columnWidth}px`;
        tempDiv.innerHTML = content;
        const height = tempDiv.offsetHeight;
        document.body.removeChild(tempDiv);
        return height + QUESTION_MARGIN;
      }

      private findBestBreakPoint(
        content: string,
        fontSize: number,
        maxHeight: number,
        columnWidth: number
      ): { fit: string; remaining: string } {
        const tempDiv = this.createTemporaryDiv();
        tempDiv.style.fontSize = `${fontSize}px`;
        tempDiv.style.width = `${columnWidth}px`;

        const paragraphs = content.split("\n");
        let fitContent = "";
        let currentContent = "";
        let remainingContent = "";

        for (let i = 0; i < paragraphs.length; i++) {
          const testContent =
            currentContent + (currentContent ? "\n" : "") + paragraphs[i];
          tempDiv.innerHTML = testContent;
          if (tempDiv.offsetHeight + QUESTION_MARGIN <= maxHeight) {
            currentContent = testContent;
            fitContent = currentContent;
          } else {
            if (i === 0) {
              const sentences = paragraphs[0].split(". ");
              currentContent = "";
              for (let j = 0; j < sentences.length; j++) {
                const testSentence =
                  currentContent + (currentContent ? ". " : "") + sentences[j];
                tempDiv.innerHTML = testSentence;
                if (tempDiv.offsetHeight + QUESTION_MARGIN <= maxHeight) {
                  currentContent = testSentence;
                  fitContent = currentContent;
                } else {
                  if (j === 0) {
                    const words = sentences[0].split(" ");
                    currentContent = "";
                    for (let k = 0; k < words.length; k++) {
                      const testWord =
                        currentContent + (currentContent ? " " : "") + words[k];
                      tempDiv.innerHTML = testWord;
                      if (tempDiv.offsetHeight + QUESTION_MARGIN <= maxHeight) {
                        currentContent = testWord;
                        fitContent = currentContent;
                      } else {
                        break;
                      }
                    }
                    remainingContent =
                      words.slice(currentContent.split(" ").length).join(" ") +
                      (sentences.length > 1
                        ? ". " + sentences.slice(1).join(". ")
                        : "") +
                      (paragraphs.length > 1
                        ? "\n" + paragraphs.slice(1).join("\n")
                        : "");
                  } else {
                    remainingContent =
                      sentences.slice(j).join(". ") +
                      (paragraphs.length > 1
                        ? "\n" + paragraphs.slice(1).join("\n")
                        : "");
                  }
                  break;
                }
              }
            } else {
              remainingContent = paragraphs.slice(i).join("\n");
            }
            break;
          }
        }

        document.body.removeChild(tempDiv);
        console.log("findBestBreakPoint:", {
          fit: fitContent,
          remaining: remainingContent.trim(),
        });
        return { fit: fitContent, remaining: remainingContent.trim() };
      }

      private moveToNextColumn(): boolean {
        if (this.currentColumn < this.currentPageColumns - 1) {
          this.currentColumn++;
          console.log("Mudando para a próxima coluna:", this.currentColumn);
          return true;
        }
        return false;
      }

      private createNewPage() {
        console.log("Criando nova página");
        if (this.currentPage.elements.length > 0) {
          this.pages.push(this.currentPage);
          console.log("Página adicionada:", this.currentPage);
        }
        const nextPageIndex = this.pages.length;
        this.currentPageColumns =
          (pageConfigs && pageConfigs[nextPageIndex]?.columns) || numeroColunas;
        this.currentPage = { elements: [], columns: this.currentPageColumns };
        this.remainingColumnHeights = Array(this.currentPageColumns).fill(
          config.effectiveHeight
        );
        this.currentColumn = 0;
      }

      private calculateElementPosition(height: number): {
        x: number;
        y: number;
      } {
        const columnWidth = this.getColumnWidth(this.currentPageColumns);
        const usedHeight =
          config.effectiveHeight -
          this.remainingColumnHeights[this.currentColumn];
        return {
          x: marginEsquerda + (columnWidth + COLUMN_GAP) * this.currentColumn,
          y: marginSuperior + usedHeight,
        };
      }

      addElement(content: string, fontSize: number) {
        console.log("Adicionando elemento com conteúdo:", content);
        let remainingContent = content;
        while (remainingContent) {
          const columnWidth = this.getColumnWidth(this.currentPageColumns);
          const { fit, remaining } = this.findBestBreakPoint(
            remainingContent,
            fontSize,
            this.remainingColumnHeights[this.currentColumn],
            columnWidth
          );
          if (fit) {
            const fitHeight = this.measureContent(
              `<div style="font-size: ${fontSize}px">${fit}</div>`,
              fontSize,
              columnWidth
            );
            const position = this.calculateElementPosition(fitHeight);
            this.currentPage.elements.push({
              element: `<div style="font-size: ${fontSize}px">${fit}</div>`,
              x: position.x,
              y: position.y,
              width: columnWidth,
              height: fitHeight,
            });
            console.log(
              "Elemento adicionado na coluna",
              this.currentColumn,
              "posição:",
              position,
              "altura:",
              fitHeight
            );
            this.remainingColumnHeights[this.currentColumn] -= fitHeight;
            remainingContent = remaining;
          }
          if (remaining && (!fit || remaining !== remainingContent)) {
            if (!this.moveToNextColumn()) {
              console.log("Passou do limite da coluna, criando nova página.");
              this.createNewPage();
            }
          } else if (!remaining) {
            break;
          }
        }
      }

      getPages(): QuestionPage[] {
        if (this.currentPage.elements.length > 0) {
          this.pages.push(this.currentPage);
        }
        console.log("Páginas geradas:", this.pages);
        return this.pages;
      }
    }

    // Função auxiliar para remover a camada extra (wrapper) se presente
    const stripOuterDiv = (html: string): string => {
      const regex = /^<div style="font-size:\s*\d+px">(.*)<\/div>$/i;
      const match = html.match(regex);
      return match ? match[1] : html;
    };

    const manager = new ContentManager();

    // Processa cada elemento de cada página do JSON
    questionPages.forEach((page: any) => {
      page.elements.forEach((el: any) => {
        let appliedFontSize = 13;
        if (el.element.includes('data-element-type="alternative"')) {
          appliedFontSize =
            typeof fontSizeAlternatives === "number"
              ? fontSizeAlternatives
              : 13;
        } else if (el.element.includes('data-element-type="question"')) {
          appliedFontSize =
            typeof fontSizeQuestoes === "number" ? fontSizeQuestoes : 13;
        }
        const contentCleaned = stripOuterDiv(el.element);
        manager.addElement(contentCleaned, appliedFontSize);
      });
    });

    const newPages = manager.getPages();
    console.log("Páginas recalculadas:", newPages);
    setQuestionPages(newPages);
    setLoading(false);
  }

  const storedQuestionPages = useRef(questionPages);
  useEffect(() => {
    storedQuestionPages.current = questionPages;
  }, [questionPages]);

  useEffect(() => {
    if (editMode) {
      recalcPagesEdit();
    } else {
      recalcPages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    provaSelect,
    numeroColunas,
    pageColumns,
    fontSizeQuestoes,
    fontSizeAlternatives,
    marginSuperior,
    marginInferior,
    marginEsquerda,
    marginDireita,
    randomAlternatives,
    randomQuestion,
    DataFrontPage,
  ]);

  useEffect(() => {
    const initialColumns = questionPages.map(() => numeroColunas);
    setPageColumns(initialColumns);
  }, [numeroColunas]);

  if (loading) {
    return (
      <div
        className="d-flex align-items-center justify-content-center vh-100"
        style={{ background: "#f8f9fa" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Carregando...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4" style={{ background: "#f8f9fa" }}>
      <style>
        {`
          @media print {
            .no-print {
              display: none !important;
            }
          }
        `}
      </style>

      {/* TOOLBAR */}
      <div className="mb-3 d-flex no-print" style={{ gap: "8px" }}>
        <button onClick={handleAddElement} className="btn btn-success">
          <Plus size={18} /> Adicionar elemento
        </button>
        <button
          onClick={handleEditElement}
          className={`btn ${
            selectedElement !== null ? "btn-info" : "btn-secondary disabled"
          }`}
          disabled={selectedElement === null}
        >
          <Edit2 size={18} /> Editar elemento
        </button>
        <button
          onClick={handleRemoveElement}
          className={`btn ${
            selectedElement !== null ? "btn-danger" : "btn-secondary disabled"
          }`}
          disabled={selectedElement === null}
        >
          <Trash2 size={18} /> Remover
        </button>
        <button onClick={recalcPages} className="btn btn-warning">
          Atualizar Layout
        </button>
        <button onClick={() => handlePrint()} className="btn btn-primary">
          Imprimir Prova
        </button>
      </div>

      {/* MODAL: Edição de elementos da capa */}
      {showEditor && selectedElement !== null && (
        <div
          style={{
            position: "fixed",
            inset: "0",
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "4px",
              padding: "24px",
              width: "100%",
              maxWidth: "900px",
            }}
          >
            <div className="mb-3">
              <h3>Editar elemento (capa)</h3>
            </div>
            <JoditEditor
              ref={editorRef}
              value={tempContent}
              config={defaultEditorConfig}
              onChange={handleEditorChange}
            />
            <div
              className="mt-3 d-flex justify-content-end"
              style={{ gap: "8px" }}
            >
              <button onClick={handleCancelEdit} className="btn btn-secondary">
                <X size={18} /> Cancelar
              </button>
              <button onClick={handleConfirmEdit} className="btn btn-success">
                <Check size={18} /> Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Edição de páginas (questões) */}
      {showPageEditor && pageBeingEdited !== null && (
        <div
          style={{
            position: "fixed",
            inset: "0",
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "4px",
              padding: "24px",
              width: "100%",
              maxWidth: "900px",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <div className="mb-3">
              <h3>Editar Página {pageBeingEdited + 1}</h3>
            </div>
            <div className="mb-3">
              <label htmlFor="numeroColunas" className="form-label">
                Número de Colunas
              </label>
              <select
                id="numeroColunas"
                className="form-select"
                value={pageEditorColumns}
                onChange={(e) => handleColumnChange(parseInt(e.target.value))}
              >
                <option value={1}>1 Coluna</option>
                <option value={2}>2 Colunas</option>
              </select>
            </div>
            <JoditEditor
              ref={editorRef}
              value={pageEditorContent}
              config={defaultEditorConfig}
              onChange={(content) => setPageEditorContent(content)}
            />
            <div
              className="mt-3 d-flex justify-content-end"
              style={{ gap: "8px" }}
            >
              <button
                onClick={handleCancelEditPage}
                className="btn btn-secondary"
              >
                <X size={18} /> Cancelar
              </button>
              <button
                onClick={handleConfirmEditPage}
                className="btn btn-success"
              >
                <Check size={18} /> Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CONTEÚDO A SER IMPRESSO */}
      <div ref={printRef}>
        {/* CAPA: Elementos da capa (dinâmicos) */}
        <div
          className="border rounded mx-auto"
          style={{
            width: A4_WIDTH,
            height: A4_HEIGHT,
            position: "relative",
            background: "#fff",
            padding: `${marginSuperior}px ${marginDireita}px ${marginInferior}px ${marginEsquerda}px`,
          }}
        >
          {DataFrontPage.map((element: PageElement, index: number) => (
            <Rnd
              key={`cover-element-${index}`}
              position={{ x: element.x, y: element.y }}
              size={{ width: element.width, height: element.height }}
              bounds="parent"
              onDragStop={(e, d) => handleDragStopCover(index, d)}
              onResizeStop={(e, direction, ref, delta, position) =>
                handleResizeCover(index, ref, position)
              }
              style={{
                border:
                  selectedElement === index
                    ? "2px solid #0d6efd"
                    : "1px solid transparent",
                cursor: "move",
              }}
              onClick={() => setSelectedElement(index)}
            >
              <div
                style={{ width: "100%", height: "100%" }}
                dangerouslySetInnerHTML={{ __html: element.element }}
              />
            </Rnd>
          ))}
        </div>

        {/* PÁGINAS DE QUESTÕES */}
        {questionPages.map((page, pageIndex) => (
          <div
            key={`question-page-${pageIndex}`}
            className={`mx-auto mb-5 ${
              borderVisible ? "border border-dark" : ""
            }`}
            style={{
              width: A4_WIDTH,
              height: A4_HEIGHT,
              position: "relative",
              background: "#fff",
              padding: `${marginSuperior}px ${marginDireita}px ${marginInferior}px ${marginEsquerda}px`,
              borderStyle: borderVisible ? "double" : "none",
              borderWidth: borderVisible ? "2px" : "0",
            }}
          >
            <button
              className="btn btn-sm btn-primary no-print"
              style={{ position: "absolute", top: 10, right: 10, zIndex: 10 }}
              onClick={() => handleEditPage(pageIndex)}
            >
              <Edit2 size={14} /> Editar Página
            </button>
            {page.elements.map((element, elementIndex) => (
              <Rnd
                key={`page-${pageIndex}-element-${elementIndex}`}
                position={{ x: element.x, y: element.y }}
                size={{ width: element.width, height: element.height }}
                bounds="parent"
                onDragStop={(e, d) =>
                  handleDragStopQuestion(pageIndex, elementIndex, d)
                }
                onResizeStop={(e, direction, ref, delta, position) =>
                  handleResizeQuestion(pageIndex, elementIndex, ref, position)
                }
                style={{ border: "1px solid transparent", cursor: "move" }}
              >
                <div
                  contentEditable
                  suppressContentEditableWarning
                  style={{
                    width: "100%",
                    height: "100%",
                    overflow: "visible",
                    fontSize: `${fontSizeQuestoes}px`,
                    outline: "none",
                    cursor: "text",
                  }}
                  onInput={(e) => {
                    const newHTML = (e.currentTarget as HTMLDivElement)
                      .innerHTML;
                    handleInlineEdit(pageIndex, elementIndex, newHTML);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      setTimeout(() => recalcPages(), 0);
                    }
                  }}
                  dangerouslySetInnerHTML={{ __html: element.element }}
                />
              </Rnd>
            ))}
            <div
              className="text-center no-print"
              style={{
                position: "absolute",
                bottom: "8px",
                left: 0,
                right: 0,
                fontSize: "0.8rem",
                color: "#666",
                userSelect: "none",
              }}
            >
              Página {pageIndex + 1} de {questionPages.length}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DiagramacaoPreview;
